import {
    useUsid,
    useShopperContext,
    useShopperContextsMutation
} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@app/hooks/use-multi-site'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'

/**
 * Custom React hook for managing and updating the shopper context's custom qualifiers.
 * Retrieves the current shopper context using the user's session ID (usid) and provides a method to update custom qualifiers.
 *
 * @returns {Object} An object containing:
 *   - updateContext {Function}: Asynchronously updates the custom qualifiers in the shopper context with the provided updates.
 *     @param {Object} customQualifiersUpdate - Key-value pairs to merge into the existing customQualifiers.
 *     @returns {Promise<void>}
 *   - isShopperContextLoading {boolean}: Indicates whether the shopper context is currently loading.
 */
const useSetShopperContextCustomQualifier = () => {
    const {usid} = useUsid()
    const {site} = useMultiSite()

    const {data: shopperContext, isLoading: isShopperContextLoading} = useShopperContext(
        {
            parameters: {usid: usid}
        },
        {
            enabled: Boolean(!isServer && usid)
        }
    )

    const updateShopperContext = useShopperContextsMutation('updateShopperContext')

    const updateContext = async (customQualifiersUpdate) => {
        if (shopperContext && shopperContext.customQualifiers) {
            // Merge the provided updates into the existing customQualifiers
            Object.assign(shopperContext.customQualifiers, customQualifiersUpdate)

            const options = {
                parameters: {
                    usid: usid,
                    siteId: site.id
                },
                body: shopperContext
            }
            await updateShopperContext.mutateAsync(options)
        }
    }

    return {
        updateContext,
        isShopperContextLoading
    }
}

/**
 * Exports the default hook for setting a custom qualifier in the shopper context.
 * @function
 * @name useSetShopperContextCustomQualifier
 * @description
 * Provides a React hook to manage and update custom qualifiers within the shopper context,
 * enabling dynamic personalization or segmentation based on custom logic.
 * @returns {Function} - The hook function for setting custom qualifiers in the shopper context.
 */
export default useSetShopperContextCustomQualifier
